import type { OpenAuthModalOptions } from "@/contexts/layout";
import { PostLoginIntentKind } from "@/domain/post-login-intent/enums";
import { requestAuthForPostLoginIntent } from "@/lib/postLoginIntent";
import type {
  OpenNewTeamModalInput,
  TeamModalOrigin,
} from "./team-modal-types";

/**
 * The ONE build-your-own flow, shared by every Build entry (landing hero,
 * netflix owned row, coverflow deck header, session-start chooser): gate
 * BEFORE opening. Signed out parks the new-team intent on the one post-login
 * carrier and raises the sign-in gate once that park is down — dismissing that
 * gate releases the intent, so an abandoned Build can never replay off a later
 * unrelated sign-in. Signed in opens the canonical modal on an empty subject.
 *
 * The signed-out branch is what makes this asynchronous, and the park-then-gate
 * order is OWNED HERE rather than by the entry points: this function awaits the
 * park and the gate goes up inside that await. So the order holds at an entry
 * whose void-typed click prop drops the promise this returns just as it does at
 * one that carries it — a call already running finishes whether or not anyone
 * holds its result. The signed-in branch reaches no `await`, so it still opens
 * the modal synchronously inside its caller's event — the batch an entry that
 * raises a latch alongside the open depends on.
 *
 * Entry points differ only in their origin geometry and their own telemetry;
 * none of them may re-implement the gate.
 */
export interface RequestNewTeamModalInput {
  isAuthenticated: boolean;
  /** Morph/focus/layer metadata of the entry that asked for the draft. */
  origin: TeamModalOrigin;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  openNew: (input: OpenNewTeamModalInput) => void;
}

export async function requestNewTeamModal(
  input: RequestNewTeamModalInput,
): Promise<void> {
  if (!input.isAuthenticated) {
    await requestAuthForPostLoginIntent({
      intent: { kind: PostLoginIntentKind.TeamModalNewTeam },
      openAuthModal: input.openAuthModal,
    });
    return;
  }

  input.openNew({ origin: input.origin });
}
